import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MemberZipaDailyTotalsDto } from '@pokerroom/types';
import { api } from '../../lib/api';

export function ZipaDailyTotals() {
  const { t } = useTranslation('member');
  // Taipei's business date starts at 06:00 (UTC +02:00 after subtracting six hours).
  const [date, setDate] = useState(() =>
    new Date(Date.now() + 2 * 3600000).toISOString().slice(0, 10),
  );
  const { data, isError, isFetching } = useQuery<MemberZipaDailyTotalsDto>({
    queryKey: ['zipa-daily-totals', date],
    queryFn: () => api.get('/members/zipa/daily-totals', { params: { date } }),
    enabled: !!date,
  });
  return (
    <section
      className="mb-6 rounded-lg border border-base-300 p-4"
      aria-label={t('zipaDailyTitle')}
    >
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-semibold">{t('zipaDailyTitle')}</h2>
        <input
          aria-label={t('zipaBusinessDate')}
          type="date"
          value={date}
          onChange={(event) => setDate(event.target.value)}
          className="input input-bordered"
        />
        <span className="text-sm text-base-content/60">{t('zipaDailyWindow')}</span>
      </div>
      {isError ? (
        <p role="alert">{t('ledgerLoadFailed')}</p>
      ) : isFetching ? (
        <p>{t('zipaDailyLoading')}</p>
      ) : (
        data && (
          <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[
              ['ledgerDeposit', data.cash.deposit],
              ['ledgerUse', data.cash.use],
              ['zipaDailyAdjust', data.nonCash.adjust],
              ['zipaDailyOpening', data.nonCash.opening],
              ['financial.receiptUse', data.nonCash.receiptUse ?? '0.00'],
              ['financial.receiptRefund', data.nonCash.receiptRefund ?? '0.00'],
              ['financial.transferIn', data.nonCash.transferIn ?? '0.00'],
              ['financial.transferOut', data.nonCash.transferOut ?? '0.00'],
              ['zipaDailyCount', String(data.lineCount)],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-sm text-base-content/60">{t(label!)}</dt>
                <dd className="font-mono">{value}</dd>
              </div>
            ))}
          </dl>
        )
      )}
    </section>
  );
}
